import { Signal, Aggregators } from 'openfin-service-signal';
import { DeferredPromise } from 'openfin-service-async';
import { Opacity, Transition } from 'openfin/_v2/shapes/shapes';

import { ServiceStore } from '../store/ServiceStore';
import { WebWindow } from '../model/WebWindow';
import { checkTolerance } from '../utils';
import { WindowManager } from '../controller/WindowManager';

import { LayouterConfig } from './LayouterConfig';
import { LayoutItem } from './Layouter';

export enum LayoutableWindowState {
  /**
   * Short-lived state whilst the LayoutableWindow's window object is being created.
   */
  INITIALIZING,

  /**
   * LayoutableWindow is waiting to be displayed, due to LayoutableWindow not being able to fit on-screen.
   *
   * When there is space on-screen, LayoutableWindow will still briefly pass-through this state once initialization is complete.
   */
  QUEUED,

  /**
   * Windows is rendered now and ready to show
   */
  RENDERED,

  /**
   * LayoutableWindow is currently visible to the user.
   *
   * LayoutableWindow will be in this state from the very start of the transition-in animation, to immediately before the
   * transition-out.
   */
  ACTIVE,

  /**
   * LayoutableWindow is still user-visible, but is now in the process of closing.
   */
  TRANSITION_OUT,

  /**
   * LayoutableWindow window has now been closed. LayoutableWindow will be placed into this state at the start of the teardown process - a
   * LayoutableWindow in this state may (very briefly) still have an associated window.
   */
  CLOSED,
}

export interface Point<T = number> {
  x: T;
  y: T;
}

export interface Rectangle {
  /**
   * Location of the top-left corner of the rectangle
   */
  origin: Point;

  /**
   * Width/height of the rectangle
   */
  size: Point;
}

export type ReadonlyRectangle = Readonly<{
  origin: Readonly<Point>;
  size: Readonly<Point>;
}>;

export abstract class LayoutableWindow implements LayoutItem {
  /**
   * Signal shared between all LayoutableWindow instances used to communicate when the mouse cursor is over a LayoutableWindow.
   *
   * All LayoutableWindow animations will be paused whilst the cursor is over a LayoutableWindow. LayoutableWindow timeouts
   * will reset once the cursor is removed.
   */
  public static readonly onFreezeToggle: Signal<[boolean]> = new Signal();

  /**
   * Signal that is dispatched whenever the LayoutableWindow changes state. LayoutableWindow states always progress linearly and cannot go
   * backwards.
   *
   * Signal will emit at-most once per state - note that some LayoutableWindows may skip some states if they are force-closed.
   *
   * Uses an aggregator so that side-effects of the state change can be captured by whoever initiated the state
   * change.
   */
  public readonly onStateChange: Signal<
    [LayoutableWindow, LayoutableWindowState],
    Promise<void>
  > = new Signal(Aggregators.AWAIT_VOID);

  protected _size: Point;
  protected readonly _maxSize!: Point;
  /**
   * Modifications to the base size of the window.
   */
  protected _sizeModifier: Point = { x: 0, y: 0 };

  private _state: LayoutableWindowState;
  private readonly _origin: Point;
  private _currentTransition: DeferredPromise | null;
  private readonly _activeTransitions: Promise<void>[];
  protected _webWindow?: Promise<WebWindow>;
  private _timeout: number;
  protected readonly _id: string;

  public get webWindow(): Promise<WebWindow> {
    if (!this._webWindow) {
      this._webWindow = this.windowManager.grab(this.windowName, this._size);
      this.postWindowGrab();
    }
    return this._webWindow;
  }

  public get maxTimeout(): number {
    return this.store.state.settings.toastDuration;
  }

  /**
   * Target bounds of the Layoutable window
   *
   * When the window is animating, this will be where the window is animating _to_, rather than the actual pixel
   * bounds of the window at that time.
   */
  public get size(): Readonly<Point> {
    return { x: this._size.x + this._sizeModifier.x, y: this._size.y + this._sizeModifier.y };
  }

  /**
   * The max size this window should be allowed to be. This is used to clamp windows sizes.
   */
  public get maxSize(): Readonly<Point> {
    return this._maxSize;
  }

  /**
   * The current state of the LayoutableWindow.
   *
   * State changes are validated to ensure the state can only advance in one direction. The {@link onStateChange}
   * signal will be emitted for each state update.
   */
  public get state(): LayoutableWindowState {
    return this._state;
  }

  /**
   * The id of this LayoutableWindow.
   */
  public get id(): string {
    return this._id;
  }

  /**
   * Flag used to identify sticky LayoutableWindows.
   *
   * Sticky LayoutableWindows do not close when `maxTimeout` expires.
   */
  public abstract get isSticky(): boolean;

  /**
   * Allows tracking of the current animation, if any.
   *
   * If the LayoutableWindow is currently in the process of animating (or applying a static window position) then this will
   * return a promise that will resolve when the window is next stationary.
   *
   * If an animation is interrupted by a second animation, this promise will persist and instead resolve once the
   * second (and any subsequent) animation is complete.
   *
   * A return value of null indicates that the window isn't currently animating or being moved.
   */
  public get currentTransition(): Promise<void> | null {
    return this._currentTransition && this._currentTransition.promise;
  }

  /**
   * The name of this LayoutableWindow.
   */
  public abstract get windowName(): string;

  public constructor(
    protected readonly store: ServiceStore,
    protected readonly windowManager: WindowManager,
    id: string
  ) {
    this._id = id;
    this._origin = { x: 0, y: 0 };
    this._size = { x: 0, y: 0 };
    this._timeout = 0;
    this._webWindow = undefined;
    this._state = LayoutableWindowState.INITIALIZING;
    this._currentTransition = null;
    this._activeTransitions = [];

    LayoutableWindow.onFreezeToggle.add(this.onFreezeToggle, this);
  }

  /**
   * Display the layoutable window.
   */
  public async show(): Promise<void> {
    const webWindow = await this.webWindow;
    await webWindow.show();
    this.unfreeze();
  }

  public async fadeIn(): Promise<void> {
    const webWindow = await this.webWindow;
    await webWindow.show();
    // We have to do these steps individually otherwise alwaysOnTop gets called first
    await webWindow.animate(
      {
        opacity: {
          duration: 300,
          opacity: 1,
        },
      },
      {
        interrupt: false,
        tween: 'ease-in-out',
      }
    );
    this.unfreeze();
  }

  public async fadeOut(): Promise<void> {
    const webWindow = await this.webWindow;
    await webWindow.animate(
      {
        opacity: {
          duration: 200,
          opacity: 0,
        },
      },
      {
        interrupt: false,
        tween: 'ease-in-out',
      }
    );
    await webWindow.hide();
    this.freeze();
  }

  /**
   * Hide the layoutable window.
   */
  public async hide(): Promise<void> {
    await (await this.webWindow).hide();
    this.unfreeze();
  }

  /**
   * Sets the LayoutableWindow to the given state. State transitions are uni-directional, will have no effect if the state is a
   * "regression" to an earlier state.
   *
   * Returns a promise that will resolve when any transition associated with this state change has completed. Will
   * return a resolved promise if there is no state change, or no animation associated with this state change.
   *
   * @param state New state of this LayoutableWindow
   */
  public async setState(state: LayoutableWindowState): Promise<void> {
    if (state > this._state) {
      this._state = state;

      const expectingAnimation =
        state === LayoutableWindowState.ACTIVE || state === LayoutableWindowState.TRANSITION_OUT;
      if (expectingAnimation && this._currentTransition === null) {
        // We want the return value of this promise to capture the animation that will be triggered by this state
        // change, but it hasn't yet started. Pre-create the transition promise in expectation of call to animate
        this._currentTransition = new DeferredPromise();
      }

      await this.onStateChange.emit(this, state);
    } else if (state < this._state) {
      console.warn(`Attempting to move layoutable window ${this.id} backward to a previous state`);
      console.warn(`(${LayoutableWindowState[this._state]} => ${LayoutableWindowState[state]})`);
    }

    await (this._currentTransition && this._currentTransition.promise);
  }

  /**
   * Animates the LayoutableWindow into a new position. The pixel-position of the LayoutableWindow must be calculated externally, by an
   * entity that has access to the full LayoutableWindow stack. The target opacity will be calculated by the LayoutableWindow itself.
   *
   * @param transform New bounds of the LayoutableWindow
   * @param config Animation timing config
   */
  public async animate(transform: ReadonlyRectangle, config: LayouterConfig): Promise<void> {
    // On Windows windows will get larger and larger when animated to the same position
    // by 1 or 2 px due to display scaling floating point errors. To avoid this we should
    // Check if the target transform matches the current bounds of the box, is so
    // ignore the animation.
    const boundsTransitionRequired = await this.checkBoundsTransitionRequired(transform);
    Object.assign(this._origin, transform.origin);

    // Calculate opacity from notification state
    let opacity: Opacity;
    if (this._state < LayoutableWindowState.TRANSITION_OUT) {
      opacity = {
        opacity: 1,
        duration: config.animDurationFadeIn,
      };
    } else {
      // Use the shorter 'move' animation duration when transitioning animation out
      opacity = { opacity: 0, duration: config.animDurationMove };
    }

    let transition: Transition = {
      opacity,
    };

    if (boundsTransitionRequired) {
      transition = {
        ...transition,
        position: {
          left: transform.origin.x,
          top: transform.origin.y,
          duration: config.animDurationMove,
        },
        size: {
          width: transform.size.x,
          height: transform.size.y,
          duration: config.animDurationMove,
        },
      };
    }

    return this.trackAnimation(
      (await this.webWindow).animate(
        {
          ...transition,
        },
        {
          interrupt: true,
          tween: 'linear',
        }
      )
    );
  }

  private async checkBoundsTransitionRequired(transform: ReadonlyRectangle): Promise<boolean> {
    const tolerance = 3;
    const webWindow = await this.webWindow;
    const bounds = await webWindow.getBounds();
    if (!bounds) {
      throw new Error('Window does not exist');
    }

    // Swapped horizontal sides
    if (!checkTolerance(tolerance, bounds.left, transform.origin.x)) {
      return true;
    }
    // Swapped vertical sides
    if (!checkTolerance(tolerance, bounds.top, transform.origin.y)) {
      return true;
    }

    // Check width
    if (!checkTolerance(tolerance, bounds.width, transform.size.x)) {
      return true;
    }
    // Check height
    if (!checkTolerance(tolerance, bounds.height, transform.size.y)) {
      return true;
    }

    // Check top
    if (!checkTolerance(tolerance, bounds.top, transform.origin.y)) {
      return true;
    }

    return false;
  }

  /**
   * Moves the LayoutableWindow to a new position without animating or affecting opacity.
   *
   * @param transform New bounds of the LayoutableWindow
   */
  public async setTransform(transform: ReadonlyRectangle): Promise<void> {
    Object.assign(this._origin, transform.origin);
    // TODO [SERVICE-739] This is a fix for setBounds not working on hidden windows.

    // NOTE [WRK-606] Changing to an animate function removed the flicker, but not sure if setBounds missing will break somewhere
    // setBounds inside animateWindow().then(HERE) still shows flicker. Seems like setBounds is causing the flicker.
    // removing it puts the toast in the top left and you see it streak across the screen before the toast shows
    // below removes the flicker and the update to createWebWindow in WindowManager removes the streak.

    const webWindow = await this.webWindow;

    async function animateWindow() {
      const transitions = {
        opacity: {
          opacity: 0,
          duration: 1,
        },
      };

      const options = {
        interrupt: true,
        tween: 'linear',
      };

      return webWindow.animate(transitions, options);
    }

    return animateWindow();
  }

  /**
   * Close LayoutableWindow window and perform cleanup.
   */
  public async close(): Promise<void> {
    this.freeze();

    LayoutableWindow.onFreezeToggle.remove(this.onFreezeToggle, this);
    this.windowManager.release(this._webWindow);
    this._webWindow = undefined;
  }

  protected abstract postWindowGrab(): void;

  private freeze(): void {
    clearTimeout(this._timeout);
    this._timeout = 0;
  }

  private unfreeze(): void {
    if (this._state <= LayoutableWindowState.ACTIVE) {
      this._timeout = window.setTimeout(this.timeoutHandler, this.maxTimeout);
    }
  }

  private readonly timeoutHandler = (): void => {
    this._timeout = 0;
    if (!this.isSticky) {
      this.setState(LayoutableWindowState.TRANSITION_OUT);
    }
  };

  protected readonly mouseEnterHandler = (): void => {
    LayoutableWindow.onFreezeToggle.emit(true);
  };

  protected readonly mouseLeaveHandler = (): void => {
    LayoutableWindow.onFreezeToggle.emit(false);
  };

  private onFreezeToggle(isFrozen: boolean): void {
    if (isFrozen) {
      this.freeze();
    } else {
      this.unfreeze();
    }
  }

  /**
   * Call this with any promise that will affect the positioning of the LayoutableWindow window.
   *
   * This util will manage `_currentTransition`, to ensure it is created and resolved/cleared at the correct times.
   *
   * @param promise Window movement/animation promise
   */
  private trackAnimation(promise: Promise<void>): Promise<void> {
    this._activeTransitions.push(promise);

    promise.then(() => {
      const index = this._activeTransitions.indexOf(promise);
      if (index >= 0) {
        this._activeTransitions.splice(index, 1);

        if (this._activeTransitions.length === 0 && this._currentTransition) {
          this._currentTransition.resolve();
          this._currentTransition = null;
        }
      }
    });

    if (!this._currentTransition) {
      this._currentTransition = new DeferredPromise();
    }

    return this._currentTransition.promise;
  }
}
